/**
 * DatePickerField — Modern sleek date picker (Iter 86o).
 *
 * Drop-in replacement for `<input type="date">` used across manager + dealer
 * screens. Shows a formatted en-IN label ("Thu, 14 Aug 2026") and opens a
 * Shadcn Calendar popover on click — no keyboard typing required.
 *
 * Props:
 *   value       ISO date string "YYYY-MM-DD" (or empty)
 *   onChange    (isoDateString) => void
 *   disabled    boolean
 *   className   optional wrapper classes for the trigger button
 *   min / max   optional ISO date string bounds
 *   testId      data-testid for the trigger
 *   placeholder shown when value is empty (default: "Pick a date")
 *
 * Consistency: same visual + format everywhere so Dealer and Manager see the
 * exact same date UX. `value`/`onChange` still speak the ISO wire format so
 * existing backend contracts stay untouched.
 */
import { useMemo, useState } from "react";
import { format, parseISO, isValid } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const toIso = (d) => (d ? format(d, "yyyy-MM-dd") : "");
const parseIsoSafe = (v) => {
  if (!v) return null;
  const d = parseISO(v);
  return isValid(d) ? d : null;
};

export default function DatePickerField({
  value,
  onChange,
  disabled = false,
  className = "",
  min,
  max,
  testId = "date-picker",
  placeholder = "Pick a date",
  ariaLabel = "Pick a date",
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parseIsoSafe(value), [value]);
  const minDate = useMemo(() => parseIsoSafe(min), [min]);
  const maxDate = useMemo(() => parseIsoSafe(max), [max]);

  const label = selected
    ? format(selected, "EEE, d MMM yyyy")
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          data-testid={testId}
          className={cn(
            "h-9 w-full justify-start gap-2 rounded-md border-border bg-background px-3 text-left font-normal",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="h-4 w-4 opacity-70" />
          <span className="truncate">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-0"
        data-testid={`${testId}-popover`}
      >
        <Calendar
          mode="single"
          selected={selected || undefined}
          onSelect={(d) => {
            if (!d) return;
            onChange?.(toIso(d));
            setOpen(false);
          }}
          initialFocus
          disabled={(date) => {
            if (minDate && date < minDate) return true;
            if (maxDate && date > maxDate) return true;
            return false;
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/** Format a "YYYY-MM-DD" string in the app's canonical en-IN display style. */
export function formatDateEnIN(iso, opts = {}) {
  const d = parseIsoSafe(iso);
  if (!d) return "";
  const { weekday = true, year = true } = opts;
  const parts = ["d MMM"];
  if (weekday) parts.unshift("EEE,");
  if (year) parts.push("yyyy");
  return format(d, parts.join(" "));
}
